import {useEffect, useState} from 'react';
import type {GetStaticPaths, GetStaticProps} from 'next';
import Head from 'next/head';
import {QueryClient, dehydrate} from '@tanstack/react-query';
import {Container} from '@chakra-ui/layout';

import {py} from '@eksab/theme';
import {setApiLocale} from '@eksab/api';
import {useTranslate} from '@eksab/i18n';
import {Competitions} from '@eksab/features/competition/components';

import {games} from '@eksab/features/play/const';
import type {GameConfig} from '@eksab/features/play/types';
import {useFiltersState} from '@eksab/features/competition/components/filters/Filters';
import {useCompetitionsQuery} from '@eksab/features/competition/hooks/useCompetitionsQuery';
import {ChallengeList} from '@eksab/features/trivia/components/ChallengeList';
import {useChallengesQuery} from '@eksab/features/trivia/hooks/useChallengesQuery';
import {SpinWheelGame} from '@eksab/features/spin-wheel';
import {useEmbedded} from '@eksab/hooks/useEmbedded';
import {useLoginMutation} from '@eksab/features/auth/hooks';
import {useRouter} from 'next/router';
import {useAccessToken} from '@eksab/hooks';
import type {CompetitionConfig} from '@eksab/features/play/types';

export default function Game({game}: {game: GameConfig}) {
  const t = useTranslate();

  const {setIsEmbedded} = useEmbedded();

  const {data: accessToken} = useAccessToken();
  const loginMutation = useLoginMutation();

  const router = useRouter();
  const wePhoneNumber = router.query['we-phone-number'];

  const [gameIsReady, setGameIsReady] = useState(false);

  useEffect(() => {
    mixpanel?.track('Page View', {
      page: 'Lobby',
      game: game.name,
      type: game.type,
    });
  }, [game.type, game.name]);

  useEffect(() => {
    if (game.name == 'trivia-misc-we' || game.type == 'spin-wheel') {
      setIsEmbedded(true);
    } else {
      setGameIsReady(true);
    }
  }, [game.name, game.type]);

  useEffect(() => {
    if (wePhoneNumber && (game.name == 'trivia-misc-we' || game.name == 'spin-wheel-we')) {
      loginMutation.mutate({
        phone_number: wePhoneNumber as string,
        region: 'EG',
        grant_type: 'we-phone-number',
      });
    }
  }, [wePhoneNumber]);

  useEffect(() => {
    if (accessToken) setGameIsReady(true);
  }, [accessToken]);

  if (game.type === 'trivia') {
    return gameIsReady ? <ChallengeList game={game} /> : null;
  }

  if (game.type === 'spin-wheel') {
    return gameIsReady ? <SpinWheelGame gameCode={game.name} /> : null;
  }

  return (
    <>
      <Head>
        <meta property="og:title" content={`${t(`app.${game.type}`)} | ${t('app.name')}`} />
        <title>{`${t(`app.${game.type}`)} | ${t('app.name')}`}</title>
      </Head>

      <Container py={py} flexDir="column" bg="white" gap={{base: 4, lg: 9}}>
        <Competitions game={game as CompetitionConfig} />
      </Container>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const locale: Locale = 'ar-EG';

  try {
    const paths = Object.values(games).map((game) => ({
      params: {
        game: game.route,
        locale,
      },
    }));

    return {
      paths,
      fallback: 'blocking',
    };
  } catch (e) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
};

export const getStaticProps: GetStaticProps = async ({params, locale}) => {
  const gameRoute = params?.game;

  if (!gameRoute) {
    return {
      notFound: true,
    };
  }

  setApiLocale(locale);
  const queryClient = new QueryClient();

  const game = Object.values(games).find((game) => game.route === gameRoute);

  if (!game) {
    return {
      notFound: true,
    };
  }

  if (game.type === 'trivia') {
    await queryClient.prefetchInfiniteQuery(useChallengesQuery.queryKey(game.name, locale), () =>
      useChallengesQuery.queryFn(game.name),
    );
  } else if (game.type === 'spin-wheel') {
    // Spin wheel offers can be prefetched if needed, but for now we skip to avoid extra requests
  } else {
    const competitionName = game.name as CompetitionConfig['name'];
    await queryClient.prefetchInfiniteQuery(
      useCompetitionsQuery.queryKey(useFiltersState.emptyState(competitionName), 'guest', locale),
      (context) => useCompetitionsQuery.queryFn(context, useFiltersState.emptyState(competitionName), false),
    );
  }

  return {
    props: {
      game,
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
    revalidate: 60 * 60 * 3,
  };
};
